
import { computed, defineComponent, reactive, ref, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { LeakViewService } from './leak-view-table.service'
import { LeakList, LeakViewQueryParams } from '@/views/dashboard/safe-status/vulnerability-fix/vulnerability-fix.service'
import RepairDrawer
  from '@/views/dashboard/safe-status/vulnerability-fix/_components/leak-view-table/_components/repair-drawer.vue'
import { VulnerabilityFixService, PatchInfo } from '@/views/dashboard/safe-status/vulnerability-fix/vulnerability-fix.service'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useRoute } from 'vue-router'
import { ClientItem } from '@/views/dashboard/_type/client-item'
import DetailsSubscribeBar from '@/views/dashboard/_components/subscribe-bar/details-subscribe-bar.vue'
import { getAuthByList, PermissionFe, ServicePermissionService } from '@/services/service-permission.service'
import { AreaType } from '@/views/dashboard/service/_enum/area-type'
import { TerminalType, TerminalTypeLabel } from '@/views/dashboard/_enum/terminal-type'
import { TerminalAuthStatus } from '@/views/dashboard/_enum/terminal-auth-status'
import PatchPopover from '@/views/dashboard/safe-status/vulnerability-fix/_components/patch-popover/patch-popover.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import { FormType, ObjectFormItem } from '@/components/cmForm/types'
import LoadMoreButton from '@/components/load-more-button.vue'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import { PropType } from 'vue'
import { resolvedVirusKbDescription } from '@/utils/virus-kb-description'
const ModulePermissionKey = {
  [AreaType.linux]: PermissionFe.linux_base_auth,
}
// 补丁编号hover不考虑抽取 因为会把补丁信息附加到表格信息里 减少查询和api多次调用
export default defineComponent({
  computed: {
    TerminalTypeLabel () {
      return TerminalTypeLabel
    }
  },
  components: {
    PatchPopover,
    DetailsSubscribeBar,
    RepairDrawer,
    SearchFilterBar,
    LoadMoreButton
  },
  props: {
    viewMode: {
      type: String as PropType<TableViewModeType>,
      default: TableViewMode.preview
    },
    scanRefresh: {
      type: Boolean,
      default: false
    },
    isTerminalInfo: {
      type: Boolean,
      default: false
    },
    terminalInfo: {
      type: Object as PropType<ClientItem>,
      default: () => ({})
    },
    /** 抽屉内打开时预填补丁编号查询（与接口 kb_number 一致，如 CVE-xxx 或纯数字） */
    initialKbNumber: {
      type: String,
      default: ''
    },
    /** 抽屉内打开时预设漏洞等级筛选：0 全部、1 可选、2 高危；未传则沿用路由 query.warn_level */
    initialWarnLevel: {
      type: Number as PropType<number | undefined>,
      default: undefined
    },
  },
  emits: ['viewAll', 'updatePreview'],
  setup (props, { emit }) {
    const { t, te, locale } = useI18n()
    const tableRef = ref()
    const injector = useReflectiveInjector([LeakViewService, VulnerabilityFixService])
    const servicePermissionService = injector.get(ServicePermissionService)
    const service = injector.get(LeakViewService)
    const mainService = injector.get(VulnerabilityFixService)
    const route = useRoute()
    const queryParams = reactive<LeakViewQueryParams>({
      page: 1,
      limit: props.viewMode === TableViewMode.preview ? 10 : 20,
      asc: false,
      sort: '',
      kb_number: '',
      device_id: props.terminalInfo.device_id,
      warn_level: props.initialWarnLevel !== undefined ? props.initialWarnLevel : (Number(route.query.warn_level) || 0),
    })

    const sortParams = reactive({
      asc: false,
      sort: ''
    })

    const hasPermission = computed(() => {
      if (props.terminalInfo.system_type !== TerminalType.linux) {
        return true
      }
      return getAuthByList(props.terminalInfo.auth_status, ModulePermissionKey[AreaType.linux]) === TerminalAuthStatus.normal
    })

    const filterFormColumns = computed<Partial<ObjectFormItem<LeakViewQueryParams, keyof LeakViewQueryParams>>>(() => ({
      warn_level: {
        colOpt: { style: 'flex: 0 0 160px' },
        label: t('safeProtectionModule.leakGrade'),
        hiddenAllLabel: true,
        disabled: () => !hasPermission.value,
        type: FormType.Select,
        outSearchFiledFlag: true,
        selectOpt: {
          filterable: true,
        },
        dictData: [
          { label: t('safeProtectionModule.leakFilterAll'), value: 0 },
          { label: t('safeProtectionModule.leakOptional'), value: 1 },
          { label: t('safeProtectionModule.leakHigh'), value: 2 },
        ],
        events: {
          change: () => getData(),
        },
      },
    }))
    const tableData = ref<LeakList[]>([])
    const isLoading = ref(false)
    const hasMore = ref(true)
    const total = ref(0)
    const patch_info = ref<PatchInfo>({} as PatchInfo)
    const repairDrawer = ref(false)
    const currentItem = ref<LeakList>({} as LeakList)
    const selectedItems = ref<LeakList[]>([])

    function isLinuxLeakItem (item: LeakList) {
      const kb = String(item?.kb_number || '').toUpperCase()
      const leakType = String((item as any)?.leak_type || '').toLowerCase()
      const os = String((item as any)?.os || '').toLowerCase()
      const systemType = Number((item as any)?.system_type)
      // 兼容不同接口口径：CVE / leak_type / os / system_type 任一命中即按 Linux 处理
      return kb.includes('CVE-') || leakType.includes('linux') || os.includes('linux') || systemType === TerminalType.linux
    }

    function getData (isLoadMore: boolean = false) {
      if (!isLoadMore) {
        queryParams.page = 1
        tableData.value = []
      }

      if (!hasPermission.value) {
        return
      }

      isLoading.value = true

      mainService.getLeakList({
        ...queryParams,
        sort: sortParams.sort,
        asc: sortParams.asc,
      }).then(response => {
        if (queryParams.page === 1) {
          tableData.value = response.list || []
        } else {
          tableData.value = [...tableData.value, ...(response.list || [])]
        }
        total.value = response.total || 0
        hasMore.value = tableData.value.length < total.value
        // 清空选中项
        nextTick(() => {
          tableRef.value?.clearSelection()
        })
        selectedItems.value = []
      }).finally(() => {
        isLoading.value = false
      })
    }
    function getPatchInfo (item: LeakList) {
      if (item.patch_info) {
        patch_info.value = item.patch_info
        return
      }
      mainService.getPatchInfo({ kb_number: item.kb_number }).then(response => {
        patch_info.value = response.patch_info
        item.patch_info = response.patch_info
      })
    }

    function displayKbDesc (row: LeakList) {
      void locale.value
      return resolvedVirusKbDescription(row.kb_number, te, t) ?? row.kb_desc ?? ''
    }

    function handlerSort ({ prop, order }: { prop: string, order: string }) {
      if (order === 'ascending') {
        sortParams.asc = true
      } else {
        sortParams.asc = false
      }
      sortParams.sort = prop
      queryParams.page = 1
      getData()
    }

    function handleSearch (value: string) {
      queryParams.kb_number = value
      queryParams.page = 1
      getData()
    }

    function handleQuery (form: any) {
      Object.assign(queryParams, form)
      queryParams.page = 1
      getData()
    }

    function handleReset () {
      queryParams.kb_number = ''
      queryParams.warn_level = props.initialWarnLevel !== undefined ? props.initialWarnLevel : (Number(route.query.warn_level) || 0)
      queryParams.page = 1
      getData()
    }

    function loadMoreData () {
      if (!hasMore.value || isLoading.value || props.viewMode === TableViewMode.preview) return
      queryParams.page = queryParams.page! + 1
      getData(true)
    }

    function handleAuthSuccess () {
      // authSuccess 事件保留，但不在 emits 中声明，因为这是内部使用
    }

    const kbPreset = (props.initialKbNumber || '').trim()
    if (kbPreset) {
      queryParams.kb_number = kbPreset
    }
    getData()
    watch(() => props.scanRefresh, (nv) => {
      nv && getData()
    })

    watch(() => props.terminalInfo, () => {
      getData()
    })

    return {
      tableRef,
      ModulePermissionKey,
      PermissionFe,
      AreaType,
      TerminalType,
      getAuthByList,
      handleAuthSuccess,
      queryParams,
      tableData,
      isLoading,
      total,
      hasMore,
      patch_info,
      repairDrawer,
      currentItem,
      selectedItems,
      filterFormColumns,
      getPatchInfo,
      displayKbDesc,
      handlerSort,
      handleSearch,
      handleQuery,
      handleReset,
      loadMoreData,
      async repairLeak (item: LeakList) {
        if (!item.patch_info) {
          await getPatchInfo(item)
        }
        currentItem.value = item
        repairDrawer.value = true
      },
      ignoreLeak (item: LeakList) {
        service.ignoreLeak({ kb_numbers: [item.kb_number] }).then(() => {
          ElMessage.success(t('safeProtectionModule.ignoreLeakSuccess'))
          if (props.viewMode === TableViewMode.full) {
            const index = tableData.value.findIndex(row => row === item)
            if (index >= 0) {
              tableData.value.splice(index, 1)
              total.value = Math.max(0, total.value - 1)
            }
            emit('updatePreview')
          } else {
            getData()
          }
        }).catch(() => {
          ElMessage.error(t('safeProtectionModule.ignoreLeakFail'))
        })
      },
      selectedCount: computed(() => selectedItems.value.length),
      handleSelectionChange (selection: LeakList[]) {
        selectedItems.value = selection
      },
      selectCancel () {
        nextTick(() => {
          tableRef.value?.clearSelection()
        })
        selectedItems.value = []
      },
      batchIgnore () {
        if (selectedItems.value.length === 0) {
          ElMessage.warning(t('safeProtectionModule.selectIgnoreFirst'))
          return
        }
        const kbNumbers = selectedItems.value.map(item => item.kb_number)
        service.ignoreLeak({ kb_numbers: kbNumbers }).then(() => {
          ElMessage.success(t('safeProtectionModule.batchIgnoreSuccess'))
          selectedItems.value = []
          getData()
          if (props.viewMode === TableViewMode.full) {
            emit('updatePreview')
          }
        }).catch(() => {
          ElMessage.error(t('safeProtectionModule.batchIgnoreFail'))
        })
      },
      async batchRepair () {
        if (selectedItems.value.length === 0) {
          ElMessage.warning(t('safeProtectionModule.selectIgnoreFirst'))
          return
        }
        const linuxItems = selectedItems.value.filter(isLinuxLeakItem)
        const linuxKbNumbers = linuxItems.map(item => item.kb_number).filter(Boolean)
        const otherKbNumbers = selectedItems.value
          .filter(item => !isLinuxLeakItem(item))
          .map(item => item.kb_number)
          .filter(Boolean)

        if (linuxKbNumbers.length > 0) {
          const listPreview = linuxKbNumbers.slice(0, 8).join(', ')
          const listSuffix = linuxKbNumbers.length > 8 ? '...' : ''
          await ElMessageBox.alert(
            t('safeProtectionModule.linuxRepairUnsupportedAlert', {
              count: linuxKbNumbers.length,
              kbNumbers: `${listPreview}${listSuffix}`
            }),
            t('common.tip'),
            { confirmButtonText: t('common.confirm') }
          )
        }

        if (otherKbNumbers.length === 0) {
          return
        }
        await service.getLeakLeakNumberRepair({
          device_id: queryParams.device_id || '',
          kb_numbers: otherKbNumbers
        })
        ElMessage.success(t('safeProtectionModule.repairNotifySent'))
      },
      handleRepairDrawerClose () {
        repairDrawer.value = false
      },
      getData,
      TableViewMode
    }
  }
})
